/**
 * <ry-button>
 *
 * Button component that renders as a native button.
 * Supports variant and size attributes.
 *
 * Usage:
 * <ry-button>Click me</ry-button>
 * <ry-button variant="ghost" size="sm">Small ghost</ry-button>
 * <ry-button modal="my-modal">Open modal</ry-button>
 *
 * No data-ry-target needed - button has no internal structure.
 */

import { RyElement } from '../core/ry-element.js';

export class RyButton extends RyElement {
  setup(): void {
    // Make it focusable and clickable like a button
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }
    this.setAttribute('role', 'button');

    // Handle click
    this.on(this, 'click', this.#handleClick);

    // Handle keyboard
    this.on(this, 'keydown', this.#handleKeydown);
  }

  #handleClick = (event: MouseEvent): void => {
    if (this.hasAttribute('disabled')) {
      event.preventDefault();
      return;
    }

    // If it has a modal attribute, the modal will handle it
    // Just emit the click event
    this.emit('click', { originalEvent: event });
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.click();
    }
  };
}

customElements.define('ry-button', RyButton);
